import { skipToken, useQuery } from "@tanstack/react-query";
import type { components } from "@jbnl/api-client";
import i18n from "@/i18n";
import { api } from "@/lib/api";

export const overzichtKey = (seizoenId: string) => ["beheer", "overzicht", seizoenId] as const;

export type Aandachtspunt = components["schemas"]["AandachtspuntDto"];

/**
 * The generated client types every `int32` as `number | string`. Narrowed once at this boundary
 * rather than spread through a page that does arithmetic on all seven.
 */
export type Overzicht = {
  seizoenId: string;
  ploegen: number;
  ondersteuners: number;
  wedstrijddagen: number;
  inschrijfperiodes: number;
  openInschrijfperiodes: number;
  indelingen: number;
  bevoegdePersonen: number;
  aandachtspunten: Aandachtspunt[];
};

/** Narrows one wire value. `Number` rather than `parseInt`: it rejects "12abc" instead of reading 12. */
function asNumber(value: number | string) {
  return typeof value === "number" ? value : Number(value);
}

/**
 * Disabled until there is a seizoen, so the screen draws its cards while the seizoenen load
 * rather than holding the page back for a number.
 */
export function useOverzicht(seizoenId: string | undefined) {
  return useQuery({
    queryKey: overzichtKey(seizoenId ?? ""),
    queryFn: !seizoenId
      ? skipToken
      : async (): Promise<Overzicht> => {
          const { data, error, response } = await api.GET(
            "/api/beheer/seizoenen/{seizoenId}/overzicht",
            { params: { path: { seizoenId } } },
          );

          if (!data) {
            const title = (error as { title?: string } | undefined)?.title;
            throw new Error(
              title ?? i18n.t("session:errors.overzicht", { status: response.status }),
            );
          }

          return {
            seizoenId: data.seizoenId,
            ploegen: asNumber(data.ploegen),
            ondersteuners: asNumber(data.ondersteuners),
            wedstrijddagen: asNumber(data.wedstrijddagen),
            inschrijfperiodes: asNumber(data.inschrijfperiodes),
            openInschrijfperiodes: asNumber(data.openInschrijfperiodes),
            indelingen: asNumber(data.indelingen),
            bevoegdePersonen: asNumber(data.bevoegdePersonen),
            aandachtspunten: data.aandachtspunten,
          };
        },
  });
}

/**
 * Where an aandachtspunt sends a beheerder. Keyed on the API's stable code rather than on its
 * sentence, which is prose the backend owns and may reword.
 */
const DESTINATION: Record<string, string> = {
  "dagen-zonder-organiserend-korps": "/wedstrijddagen",
  "periode-niet-aangekondigd": "/inschrijfperiodes",
  "klasse-niveau-zonder-bezetting": "/stamgegevens/functies",
  "niveau-zonder-dagbezetting": "/stamgegevens/functies",
  "functie-zonder-bevoegde": "/bevoegdheden",
  // The dag is opened by naming it on a periode, not by editing the dag itself.
  "dagen-zonder-ondersteunerperiode": "/inschrijfperiodes",
};

export function aandachtspuntLink(code: string) {
  return DESTINATION[code];
}
